import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { API_URL } from './constants';

// Constants for storage keys
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

// Check if running in Capacitor native platform
export const isNative = Capacitor.isNativePlatform();

// Token management functions for native apps
export const saveTokens = async (accessToken: string, refreshToken: string): Promise<void> => {
  if (!isNative) return;

  await Preferences.set({ key: ACCESS_TOKEN_KEY, value: accessToken });
  await Preferences.set({ key: REFRESH_TOKEN_KEY, value: refreshToken });
};

export const getAccessToken = async (): Promise<string | null> => {
  if (!isNative) return null;

  const { value } = await Preferences.get({ key: ACCESS_TOKEN_KEY });
  return value;
};

export const getRefreshToken = async (): Promise<string | null> => {
  if (!isNative) return null;

  const { value } = await Preferences.get({ key: REFRESH_TOKEN_KEY });
  return value;
};

export const clearTokens = async (): Promise<void> => {
  if (!isNative) return;

  await Preferences.remove({ key: ACCESS_TOKEN_KEY });
  await Preferences.remove({ key: REFRESH_TOKEN_KEY });
};

// Refresh token function for native apps
export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    const refreshToken = await getRefreshToken();
    
    if (!refreshToken) {
      return false;
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-native-app': 'true'
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      // If refresh fails, we need to re-authenticate
      return false;
    }

    const data = await response.json();
    
    // Handle both response formats - direct tokens or nested in data object
    const accessToken = data.accessToken || data.tokens?.accessToken;
    const newRefreshToken = data.refreshToken || data.tokens?.refreshToken;
    
    if (accessToken && newRefreshToken) {
      await saveTokens(accessToken, newRefreshToken);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  }
};

// Helper to add auth info to fetch requests
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  // Start with the provided options or default to empty object
  const fetchOptions: RequestInit = { ...options };
  
  // Initialize headers if not present
  fetchOptions.headers = fetchOptions.headers || {};
  
  // For native apps, use token-based auth
  if (isNative) {
    let accessToken = await getAccessToken();
    
    // Add headers
    fetchOptions.headers = {
      ...fetchOptions.headers,
      'x-native-app': 'true',
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
    };

    // Make the request
    let response = await fetch(url, fetchOptions);
    
    // If unauthorized and we have a refresh token, try to refresh and retry
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      
      if (refreshed) {
        // Get the new access token
        accessToken = await getAccessToken();
        
        // Update the Authorization header with the new token
        const headers = { ...fetchOptions.headers } as Record<string, string>;
        headers['Authorization'] = `Bearer ${accessToken}`;
        
        // Retry the request with the new token
        return fetch(url, { ...fetchOptions, headers });
      }
    }
    
    return response;
  } 
  
  // For browser/PWA, use cookie-based auth
  return fetch(url, {
    ...fetchOptions,
    credentials: 'include',
  });
}; 